/**
 * The Watcher's utterance, spoken in the air.
 *
 * ## Why this exists at all — latency as liturgy
 *
 * A vision reply arrives seconds after its frame was taken. That delay is
 * unfixable and is deliberately NOT hidden. Instead it is split in two:
 *
 *  - **The line is spoken now**, here, in screen space. Close, present,
 *    unhurried. No anchoring needed, so nothing waits.
 *  - **The writing appears where you were.** The same text is anchored, from
 *    the pose the frame was captured at, onto the object it named. Turn back
 *    and it is already on the thing — as though it had been written before the
 *    participant was told to look.
 *
 * The system therefore never appears to compute. It appears to have known. A
 * faster model would make this worse, not better.
 *
 * ## Rules this obeys
 *
 * **Exactly one, ever.** Authority comes from scarcity — this is the opposite
 * of the ambient fragment field in every respect: singular where they are
 * plural, bright where they are dim, briefly held at the lens where they are
 * committed to the room. Two of these on screen at once and it becomes a
 * notification feed.
 *
 * **Gold, and gold is nothing else.** See pigments.ts.
 *
 * **Second person, liturgical cadence.** Typed rather than shown, with a beat
 * at each punctuation mark, because the piece's voice is spoken rather than
 * printed. The Rite's `Voice` does the same and for the same reason; this is a
 * separate component only because it has a different lifecycle (arrives
 * unbidden, fades on its own) rather than a different intent.
 */

import { useEffect, useRef, useState } from 'react';
import { useStore } from '../../state/store.js';

/** ms per character. Slower than the Rite — this is meant to be dwelt on. */
const TYPE_MS = 52;
/** Extra beat after a comma or a full stop. */
const PUNCT_MS = 240;
/** How long the finished line is held before it begins to go. */
const HOLD_MS = 4200;
/** Fade duration. Matches the CSS transition. */
const FADE_MS = 1600;

type Phase = 'typing' | 'holding' | 'leaving' | 'gone';

export function Directive() {
  const observation = useStore((s) => s.lastObservation);
  const [shown, setShown] = useState('');
  const [phase, setPhase] = useState<Phase>('gone');
  // Identity of the utterance currently being spoken, so a re-render does not
  // restart the typing and a genuinely new line always does.
  const spokenRef = useRef<string | null>(null);

  useEffect(() => {
    // The stub is a legitimate fallback but it is not the Watcher speaking —
    // giving four hardcoded lines the gold treatment would be a lie about what
    // just happened. They still enter the field as ordinary fragments.
    if (!observation || observation.via !== 'llm' || !observation.text) return;
    const text = observation.text;
    if (spokenRef.current === text) return;
    spokenRef.current = text;

    let cancelled = false;
    const timers: number[] = [];
    setShown('');
    setPhase('typing');

    let i = 0;
    const step = () => {
      if (cancelled) return;
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        setPhase('holding');
        timers.push(
          window.setTimeout(() => {
            if (cancelled) return;
            setPhase('leaving');
            timers.push(
              window.setTimeout(() => !cancelled && setPhase('gone'), FADE_MS),
            );
          }, HOLD_MS),
        );
        return;
      }
      const ch = text[i - 1] ?? '';
      timers.push(window.setTimeout(step, TYPE_MS + (/[.,;:—]/.test(ch) ? PUNCT_MS : 0)));
    };
    timers.push(window.setTimeout(step, 260));

    return () => {
      cancelled = true;
      for (const t of timers) window.clearTimeout(t);
    };
  }, [observation]);

  if (phase === 'gone' || !shown) return null;

  return (
    <div className={`directive ${phase}`} aria-live="polite">
      <p className="directive-line">
        {shown}
        {phase === 'typing' && <span className="directive-caret" />}
      </p>
    </div>
  );
}
